import { useState } from "react";
import type { Rule, RulePredicate, ActionKind, Preset, Weekday } from "../api/types";
import { WEEKDAYS } from "../api/types";
import { useCreateRule, useDeleteRule, usePresets, useUpdateRule } from "../api/queries";
import { ApiError } from "../api/client";

interface Props {
  rule: Rule | null;  // null -> create
  onDone: () => void;
}

const EVENT_TYPES = ["default", "outOfOffice", "focusTime", "workingLocation", "fromGmail"] as const;
const MY_RESPONSE_VALUES = ["accepted", "declined", "tentative", "needsAction"] as const;
const VISIBILITY_VALUES = ["default", "public", "private", "confidential"] as const;

const activeInputClasses = "border-emerald-500/60 ring-1 ring-emerald-500/40";

export default function RuleEditor({ rule, onDone }: Props) {
  const presets = usePresets();
  const createMut = useCreateRule();
  const updateMut = useUpdateRule();
  const deleteMut = useDeleteRule();

  const [name, setName] = useState(rule?.name ?? "");
  const [enabled, setEnabled] = useState(rule?.enabled ?? true);
  const [actionKind, setActionKind] = useState<ActionKind>(rule?.action_kind ?? "preset");
  const [presetId, setPresetId] = useState<number | null>(rule?.preset_id ?? null);
  const [predicate, setPredicate] = useState<RulePredicate>(rule?.predicate ?? {});
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setError(null);
    try {
      if (!name) throw new Error("name required");
      if (actionKind === "preset" && presetId == null) throw new Error("preset required when action_kind=preset");
      const needsNullPreset = actionKind === "skip" || actionKind === "do_nothing";
      if (rule) {
        await updateMut.mutateAsync({
          id: rule.id,
          name,
          enabled,
          predicate,
          action_kind: actionKind,
          preset_id: actionKind === "preset" ? presetId : null,
          clear_preset: needsNullPreset,
        });
      } else {
        await createMut.mutateAsync({
          name, predicate, action_kind: actionKind,
          preset_id: actionKind === "preset" ? presetId : null,
        });
      }
      onDone();
    } catch (err) {
      if (err instanceof ApiError) setError(formatApiDetail(err.detail) || err.message);
      else if (err instanceof Error) setError(err.message);
      else setError("save failed");
    }
  }

  function formatApiDetail(detail: unknown): string {
    if (detail == null) return "";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
      // FastAPI 422 body: [{ loc, msg, type }, ...]
      const msgs = detail
        .map((d: unknown) => {
          if (d && typeof d === "object") {
            const r = d as { loc?: unknown; msg?: unknown };
            const loc = Array.isArray(r.loc) ? r.loc.filter((x) => x !== "body").join(".") : "";
            const msg = typeof r.msg === "string" ? r.msg : JSON.stringify(r);
            return loc ? `${loc}: ${msg}` : msg;
          }
          return String(d);
        })
        .filter(Boolean);
      return msgs.join("; ");
    }
    try {
      return JSON.stringify(detail);
    } catch {
      return String(detail);
    }
  }

  async function remove() {
    if (!rule) return;
    if (!confirm(`Delete rule "${rule.name}"?`)) return;
    await deleteMut.mutateAsync(rule.id);
    onDone();
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Name">
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="rule-name"
          />
        </Field>
        <Field label="Enabled">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="h-4 w-4 accent-brand"
            />
            active
          </label>
        </Field>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Action">
          <div className="flex gap-2">
            {(["preset", "skip", "do_nothing"] as const).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setActionKind(k)}
                className={`rounded-md border px-3 py-1 text-sm ${
                  actionKind === k
                    ? "border-brand bg-brand/20 text-brand"
                    : "border-slate-700 bg-slate-800 text-slate-300 hover:border-slate-600"
                }`}
              >
                {k}
              </button>
            ))}
          </div>
        </Field>
        {actionKind === "preset" ? (
          <Field label="Preset">
            <select
              className="input"
              value={presetId ?? ""}
              onChange={(e) => setPresetId(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">— choose —</option>
              {(presets.data?.presets ?? []).map((p: Preset) => (
                <option key={p.id} value={p.id}>
                  {p.name} · {p.color} · lum {p.luminance} · {p.effect}
                </option>
              ))}
            </select>
          </Field>
        ) : null}
      </div>

      <fieldset className="space-y-3 rounded-xl border border-slate-800 bg-slate-900/40 p-4">
        <legend className="px-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
          Calendar event conditions
        </legend>
        <p className="text-xs text-slate-500">
          Match against the properties of a calendar event. Leave blank to ignore.
        </p>

        <Field label="Title regex (empty = any)" active={!!predicate.title_regex}>
          <input
            className={`input font-mono ${predicate.title_regex ? activeInputClasses : ""}`}
            value={predicate.title_regex ?? ""}
            onChange={(e) => setPredicate({ ...predicate, title_regex: e.target.value || null })}
            placeholder="e.g. focus|deep work"
          />
        </Field>

        <Field label="Calendar name regex (empty = any)" active={!!predicate.calendar_name_regex}>
          <input
            className={`input font-mono ${predicate.calendar_name_regex ? activeInputClasses : ""}`}
            value={predicate.calendar_name_regex ?? ""}
            onChange={(e) =>
              setPredicate({ ...predicate, calendar_name_regex: e.target.value || null })
            }
            placeholder="e.g. Work|Personal"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <TriCheck
            label="all day"
            value={predicate.all_day}
            onChange={(v) => setPredicate({ ...predicate, all_day: v })}
          />
          <TriCheck
            label="primary calendar"
            value={predicate.is_primary_calendar}
            onChange={(v) => setPredicate({ ...predicate, is_primary_calendar: v })}
          />
          <TriCheck
            label="I am organizer"
            value={predicate.organizer}
            onChange={(v) => setPredicate({ ...predicate, organizer: v })}
          />
        </div>

        <Field
          label="My response (multi-select; empty = any)"
          active={(predicate.my_response?.length ?? 0) > 0}
        >
          <div className="flex flex-wrap gap-2">
            {MY_RESPONSE_VALUES.map((r) => {
              const selected = predicate.my_response?.includes(r) ?? false;
              return (
                <button
                  key={r}
                  type="button"
                  onClick={() => {
                    const current = predicate.my_response ?? [];
                    const next = selected ? current.filter((x) => x !== r) : [...current, r];
                    setPredicate({ ...predicate, my_response: next.length ? next : null });
                  }}
                  className={`rounded-md border px-2 py-1 text-xs ${
                    selected
                      ? "border-brand bg-brand/20 text-brand"
                      : "border-slate-700 bg-slate-800 text-slate-300"
                  }`}
                >
                  {r}
                </button>
              );
            })}
          </div>
        </Field>

        <Field
          label="Visibility (multi-select; empty = any)"
          active={(predicate.visibility?.length ?? 0) > 0}
        >
          <div className="flex flex-wrap gap-2">
            {VISIBILITY_VALUES.map((v) => {
              const selected = predicate.visibility?.includes(v) ?? false;
              return (
                <button
                  key={v}
                  type="button"
                  onClick={() => {
                    const current = predicate.visibility ?? [];
                    const next = selected ? current.filter((x) => x !== v) : [...current, v];
                    setPredicate({ ...predicate, visibility: next.length ? next : null });
                  }}
                  className={`rounded-md border px-2 py-1 text-xs ${
                    selected
                      ? "border-brand bg-brand/20 text-brand"
                      : "border-slate-700 bg-slate-800 text-slate-300"
                  }`}
                >
                  {v}
                </button>
              );
            })}
          </div>
        </Field>

        <Field label="Transparency" active={predicate.transparency != null}>
          <select
            className={`input ${predicate.transparency != null ? activeInputClasses : ""}`}
            value={predicate.transparency ?? ""}
            onChange={(e) =>
              setPredicate({
                ...predicate,
                transparency: (e.target.value || null) as "opaque" | "transparent" | null,
              })
            }
          >
            <option value="">any</option>
            <option value="opaque">opaque</option>
            <option value="transparent">transparent</option>
          </select>
        </Field>

        <Field label="Status" active={predicate.status != null}>
          <select
            className={`input ${predicate.status != null ? activeInputClasses : ""}`}
            value={predicate.status ?? ""}
            onChange={(e) =>
              setPredicate({
                ...predicate,
                status: (e.target.value || null) as "confirmed" | "cancelled" | "tentative" | null,
              })
            }
          >
            <option value="">any</option>
            <option value="confirmed">confirmed</option>
            <option value="tentative">tentative</option>
            <option value="cancelled">cancelled</option>
          </select>
        </Field>

        <Field
          label="Event type (multi-select; empty = any)"
          active={(predicate.event_type?.length ?? 0) > 0}
        >
          <div className="flex flex-wrap gap-2">
            {EVENT_TYPES.map((t) => {
              const selected = predicate.event_type?.includes(t) ?? false;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => {
                    const current = predicate.event_type ?? [];
                    const next = selected ? current.filter((x) => x !== t) : [...current, t];
                    setPredicate({ ...predicate, event_type: next.length ? next : null });
                  }}
                  className={`rounded-md border px-2 py-1 text-xs ${
                    selected
                      ? "border-brand bg-brand/20 text-brand"
                      : "border-slate-700 bg-slate-800 text-slate-300"
                  }`}
                >
                  {t}
                </button>
              );
            })}
          </div>
        </Field>
      </fieldset>

      <fieldset className="space-y-3 rounded-xl border border-slate-800 bg-slate-900/40 p-4">
        <legend className="px-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
          Rule schedule
        </legend>
        <p className="text-xs text-slate-500">
          When is this rule allowed to fire? Evaluated against the current time, not against event times.
        </p>

        <Field
          label="Days of week (multi-select; empty = any)"
          active={(predicate.days_of_week?.length ?? 0) > 0}
        >
          <div className="flex flex-wrap gap-2">
            {WEEKDAYS.map((d: Weekday) => {
              const selected = predicate.days_of_week?.includes(d) ?? false;
              return (
                <button
                  key={d}
                  type="button"
                  onClick={() => {
                    const current = predicate.days_of_week ?? [];
                    const next = selected ? current.filter((x) => x !== d) : [...current, d];
                    setPredicate({ ...predicate, days_of_week: next.length ? next : null });
                  }}
                  className={`rounded-md border px-2 py-1 text-xs ${
                    selected
                      ? "border-brand bg-brand/20 text-brand"
                      : "border-slate-700 bg-slate-800 text-slate-300"
                  }`}
                >
                  {d.slice(0, 3)}
                </button>
              );
            })}
          </div>
        </Field>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Start time (HH:MM, 24h)" active={!!predicate.start_time}>
            <input
              type="time"
              className={`input ${predicate.start_time ? activeInputClasses : ""}`}
              value={predicate.start_time ?? ""}
              onChange={(e) =>
                setPredicate({ ...predicate, start_time: e.target.value || null })
              }
              step={60}
            />
          </Field>
          <Field label="End time (HH:MM, 24h)" active={!!predicate.end_time}>
            <input
              type="time"
              className={`input ${predicate.end_time ? activeInputClasses : ""}`}
              value={predicate.end_time ?? ""}
              onChange={(e) =>
                setPredicate({ ...predicate, end_time: e.target.value || null })
              }
              step={60}
            />
          </Field>
        </div>
        <p className="text-xs text-slate-500">
          Set both times together. Wrap-around windows (e.g. 22:00 → 06:00) are supported.
        </p>
      </fieldset>

      <fieldset className="space-y-3 rounded-xl border border-slate-800 bg-slate-900/40 p-4">
        <legend className="px-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
          System conditions
        </legend>
        <p className="text-xs text-slate-500">
          Match properties of the current tick (derived from your Working Hours config), not a
          specific event. A rule whose only predicates are system conditions can still fire when
          the event cache is empty.
        </p>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <TriCheck
            label="no events"
            value={predicate.no_events}
            onChange={(v) => setPredicate({ ...predicate, no_events: v })}
          />
          <TriCheck
            label="no effective events"
            value={predicate.no_effective_events}
            onChange={(v) => setPredicate({ ...predicate, no_effective_events: v })}
          />
          <TriCheck
            label="working hours"
            value={predicate.working_hours}
            onChange={(v) => setPredicate({ ...predicate, working_hours: v })}
          />
          <TriCheck
            label="not working day"
            value={predicate.not_working_day}
            onChange={(v) => setPredicate({ ...predicate, not_working_day: v })}
          />
        </div>
      </fieldset>

      {error ? <div className="text-sm text-rose-400">{error}</div> : null}

      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={save} className="btn-primary">
          {rule ? "Save changes" : "Create rule"}
        </button>
        {rule ? (
          <button type="button" onClick={remove} className="btn-danger">
            Delete
          </button>
        ) : null}
        <button type="button" onClick={onDone} className="btn-secondary">
          Cancel
        </button>
      </div>
    </div>
  );
}

function Field({
  label,
  children,
  active,
}: {
  label: string;
  children: React.ReactNode;
  active?: boolean;
}) {
  return (
    <label className="block">
      <div
        className={`mb-1 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide ${
          active ? "text-emerald-400" : "text-slate-400"
        }`}
      >
        {active ? <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> : null}
        {label}
      </div>
      {children}
    </label>
  );
}

function TriCheck({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean | null | undefined;
  onChange: (v: boolean | null) => void;
}) {
  const state = value === true ? "true" : value === false ? "false" : "any";
  const active = value === true || value === false;
  return (
    <label className="block">
      <div
        className={`mb-1 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide ${
          active ? "text-emerald-400" : "text-slate-400"
        }`}
      >
        {active ? <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> : null}
        {label}
      </div>
      <select
        className={`input ${active ? activeInputClasses : ""}`}
        value={state}
        onChange={(e) => {
          const v = e.target.value;
          onChange(v === "any" ? null : v === "true");
        }}
      >
        <option value="any">any</option>
        <option value="true">true</option>
        <option value="false">false</option>
      </select>
    </label>
  );
}
